import type { RouteRecordRaw } from 'vue-router'
import { uniqWith, isEqual } from 'lodash-es'
import { router } from '@/router/index'
import { useResource } from '@/hooks/useResource'
import { cloneDeep, recursion } from '@/utils/tools'
import { useTemCache } from '@/stores/modules/temCache'

const modules = import.meta.glob('../views/**/*.vue')

// 菜单不展示的路由
export const hiddenMenuList: any[] = [
  '/customDetailPage',
  '/highSeaDetail',
  '/customerDetail',
  '/recycleBinDetail',
  '/callCenter/cloudGroupCallLossData',
  '/callCenter/turnCallDetail',
  '/callCenter/cloudGroupCallDetail',
  '/clueDetail',
  '/clueHighSeasDetail',
  '/clueRecycleDetail',
  '/inquiryManageDetail',
  '/appointVisitDetail',
  '/signManageDetail',
  '/billDetail',
  '/receivablesManageDetail',
  '/erp/moneylendingDetail',
  '/bpm/moneylendingDetail'
]

// 需要重命名的菜单
export const reNameMenuList: any[] = [
  '/customerDetail',
  '/highSeaDetail',
  '/recycleBinDetail',
  '/callCenter/turnCallDetail',
  '/callCenter/cloudGroupCallDetail',
  '/clueDetail',
  '/clueRecycleDetail',
  '/clueHighSeasDetail',
  '/inquiryManageDetail'
]

// 本地路由渲染为菜单
export function formatLocalMenu(routes: any[], path: string = '') {
  return routes.map((el: any) => {
    el.path = path + '/' + el.path
    if (el.children) {
      el.children = formatLocalMenu(el.children, el.path)
    } else {
      el.children = []
    }
    return {
      icon: el?.meta?.icon,
      name: el?.meta?.title,
      path: el.path,
      children: el.children
    }
  })
}

// 动态路由渲染为菜单
export function formatOriginMenu(routes: any[], systemCode: string, path: string = '') {
  return routes?.map((el: any) => {
    // 自定义应用需要将menuId,code拼接到path上 10预定义 20,21自定义 30固定型
    if (el.defineType === 20 || el.defineType === 21) {
      el.path = `/customPage/${el.id}/${el.appCode}`
    } else if (el.defineType === 10) {
      // 10预定义应用需要拼接menuId
      el.path = `${path ? path : ''}/${el.path}/${el.id}`
    } else if (el.path?.indexOf('/') === -1) {
      // 兼容一下原来的菜单配置
      el.showed && (el.path = path + '/' + el.path)
    } else {
      el.path = '/' + (el.path || '') // 菜单组没有path
    }
    // 手动去重
    el.path = '/' + uniqueArray(el.path.split('/') || []).join('/')
    // 避免出现两条/在一起
    el.path = el.path.replace(/\/\//g, '/')
    if (el.children) {
      el.children = formatOriginMenu(el.children, systemCode, el.path)
    } else {
      el.children = []
    }
    try {
      const dataScope = JSON.parse(el.dataScope)
      el.queryOption = dataScope.dataScope
      el.defaultValues = dataScope.paramsConfigs
    } catch (e) {
      el.queryOption = ''
      el.defaultValues = ''
    }

    return {
      ...el,
      resourceList: el?.resourceVOList || [],
      systemCode,
      tableCode: el.appCode,
      queryOption: el.queryOption || '',
      defaultValues: el.defaultValues
    }
  })
}

// 获取component文件路径
const loadView = (viewPath: string) => {
  return modules[`../views${viewPath}/index.vue`]
}

// 动态路由渲染为路由
export function formatOriginRoute(routes: any[], path: string = '') {
  return routes.map((el: any) => {
    el.path = path + '/' + el.path
    if (el.children) {
      el.children = formatOriginRoute(el.children, el.path)
    } else {
      el.children = []
    }
    return {
      name: el.code,
      path: el.path,
      meta: {
        icon: el.icon,
        title: el.name
      },
      children: el.children,
      component: loadView(el.path)
    }
  })
}
// 动态添加路由
export function renderSyncRoutes(routes: RouteRecordRaw[]) {
  routes.map(route => {
    router.addRoute('Layout', route)
  })
}

// 设置筛选条件增加页面默认配置的筛选条件组
export function setSearchInfoExtend(
  data: Record<string, any>,
  fn: Fn,
  initThroughData?: { initValue: Recordable; key: string }
) {
  const { getQueryOption } = useResource()
  const queryData = cloneDeep(data)
  // 没有condition就初始化一个
  if (!queryData?.condition?.groups?.length) {
    queryData.condition = {
      conjunction: 'and',
      groups: []
    }
  }
  const queryOption = getQueryOption() // 获取到自定义菜单条件
  queryOption?.groups?.forEach((item: Record<string, any>) => {
    if (!(item.values instanceof Array)) {
      item.values = [item.values]
    }
  })
  // 增加菜单默认查询条件
  if (queryOption?.groups?.length) {
    queryData.condition.groups.push(queryOption)
  }
  // 增加高级筛选的数据
  if (data?.condition?.groups?.length) {
    queryData.condition.groups.concat(data.condition.groups)
  }
  //
  if (!queryData?.condition?.groups?.length) {
    queryData.condition = undefined
  }

  // 【郭攀-2001246】如果存在初始穿透数据，则初始值始终携带在请求参数中
  if (initThroughData) {
    const temCache = useTemCache()
    const customerParams = temCache.getData(initThroughData.key)

    if (customerParams) {
      if (queryData?.condition?.groups) {
        initThroughData.initValue?.groups?.forEach((item: any) => {
          queryData.condition.groups.push(item)
        })
        // 对象数组去重
        queryData.condition.groups = uniqWith(queryData.condition.groups, isEqual)
      } else {
        queryData.condition = initThroughData.initValue
      }
    }
  }

  fn({ ...queryData })
}

export function getValueByMenu(id: string) {
  const { getDefaultValues } = useResource()
  const defaultValues = getDefaultValues()
  if (defaultValues?.length) {
    const res = defaultValues.find((el: Record<string, any>) => el.left === id)
    return res?.right
  }
}

/**
 *  通过appCode去获取菜单名称，当appcode匹配到多个菜单时，默认返回预定义的菜单，否则返回匹配到的第一个菜单
 */
export function getMenuByAppcode(list: Recordable[], appCode: string) {
  const result: Recordable[] = []
  recursion(list, menu => {
    if (menu.appCode === appCode) {
      result.push(menu)
    }
  })
  if (result.length > 1) {
    const res = result.find(menu => !menu.defined) // 非自定义
    return res || result[0] || {}
  } else {
    return result[0] || {}
  }
}

/**
 *  通过appCode去获取菜单名称，当appcode匹配到多个菜单时，默认返回预定义的菜单，否则返回匹配到的第一个菜单
 */
export function getMenuByMenuId(list: Recordable[], menuId: string) {
  let result: Recordable = {}
  recursion(list, menu => {
    if (menu.id === menuId) {
      result = { ...menu }
    }
  })
  return result || {}
}

function uniqueArray(list: string[]) {
  const result: string[] = []
  list.forEach(i => {
    if (!result.includes(i)) {
      result.push(i)
    }
  })
  return result
}
